import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateTeamName = (teamName) => {
    if (!teamName || typeof teamName !== 'string' || teamName.trim() === '') {
        throw new Error('Team name is required and must be a valid string');
    }

    if (teamName.trim().length > 100) {
        throw new Error('Team name cannot exceed 100 characters');
    }

    return teamName.trim();
};

const validateTeamDescription = (description) => {
    if (description !== null && description !== undefined) {
        if (typeof description !== 'string') {
            throw new Error('Team description must be a string');
        }
        if (description.trim().length > 500) {
            throw new Error('Team description cannot exceed 500 characters');
        }
        return description.trim();
    }
    return '';
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleTeamApiError = (error, operation = 'Team API operation') => {
    console.error(`[TeamAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        throw new Error('Team not found or no longer available');
    }

    if (error.response?.status === 409) {
        throw new Error('Team already exists or conflicts with existing data');
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// TEAM CRUD OPERATIONS - SINGULAR /api/team/
// ============================================================================

/**
 * Create a new team
 * POST /api/team/
 * @param {Object} teamData - { name, description, phoneNumber, address }
 * @returns {Promise<Object>} Created team data
 */
export const createTeam = async (teamData) => {
    try {
        const validName = validateTeamName(teamData.name);
        const validDescription = validateTeamDescription(teamData.description);

        console.log('[TeamAPI] Creating team:', validName);

        const response = await apiClient.post(`${API_BASE_URL}/api/team/`, {
            name: validName,
            description: validDescription,
            phoneNumber: teamData.phoneNumber ? teamData.phoneNumber.trim() : undefined,
            address: teamData.address ? teamData.address.trim() : undefined
        });

        console.log('[TeamAPI] Team created successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error creating team:', error);
        handleTeamApiError(error, 'Creating team');
    }
};

/**
 * Get team details by ID
 * GET /api/team/:teamId
 * @param {string} teamId - Team ID
 * @returns {Promise<Object>} Team data with member count
 */
export const getTeam = async (teamId) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Getting team details:', validTeamId);

        const response = await apiClient.get(`${API_BASE_URL}/api/team/${validTeamId}`);

        console.log('[TeamAPI] Team details retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error getting team details:', error);
        handleTeamApiError(error, 'Getting team details');
    }
};

/**
 * Update team details
 * PUT /api/team/:teamId
 * @param {string} teamId - Team ID
 * @param {Object} updateData - { name?, description?, phoneNumber?, address?, settings? }
 * @returns {Promise<Object>} Updated team data
 */
export const updateTeam = async (teamId, updateData) => {
    try {
        const validTeamId = validateTeamId(teamId);

        const validUpdateData = {};

        if (updateData.name !== undefined) {
            validUpdateData.name = validateTeamName(updateData.name);
        }

        if (updateData.description !== undefined) {
            validUpdateData.description = validateTeamDescription(updateData.description);
        }

        if (updateData.phoneNumber !== undefined) {
            validUpdateData.phoneNumber = updateData.phoneNumber ? updateData.phoneNumber.trim() : null;
        }

        if (updateData.address !== undefined) {
            validUpdateData.address = updateData.address ? updateData.address.trim() : null;
        }

        if (updateData.settings !== undefined) {
            validUpdateData.settings = updateData.settings;
        }

        if (updateData.memberPrice !== undefined) {
            validUpdateData.memberPrice = updateData.memberPrice;
        }

        if (updateData.maxTeamMembers !== undefined) {
            validUpdateData.maxTeamMembers = updateData.maxTeamMembers;
        }

        console.log('[TeamAPI] Updating team:', validTeamId);

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team/${validTeamId}`,
            validUpdateData
        );

        console.log('[TeamAPI] Team updated successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error updating team:', error);
        handleTeamApiError(error, 'Updating team');
    }
};

/**
 * Delete a team
 * DELETE /api/team/:teamId
 * @param {string} teamId - Team ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteTeam = async (teamId) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Deleting team:', validTeamId);

        const response = await apiClient.delete(`${API_BASE_URL}/api/team/${validTeamId}`);

        console.log('[TeamAPI] Team deleted successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error deleting team:', error);
        handleTeamApiError(error, 'Deleting team');
    }
};

/**
 * Get team statistics
 * GET /api/team/:teamId/stats
 * @param {string} teamId - Team ID
 * @returns {Promise<Object>} Team stats (totalMembers, activeMembers, etc.)
 */
export const getTeamStats = async (teamId) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Getting team stats:', validTeamId);

        const response = await apiClient.get(`${API_BASE_URL}/api/team/${validTeamId}/stats`);

        console.log('[TeamAPI] Team stats retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error getting team stats:', error);
        handleTeamApiError(error, 'Getting team stats');
    }
};

/**
 * Get current user's teams
 * GET /api/team/my-teams
 * @returns {Promise<Object>} Array of user's teams with membership info
 */
export const getUserTeams = async () => {
    try {
        console.log('[TeamAPI] Getting user teams');

        const response = await apiClient.get(`${API_BASE_URL}/api/team/my-teams`);

        console.log('[TeamAPI] User teams retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error getting user teams:', error);
        handleTeamApiError(error, 'Getting user teams');
    }
};

/**
 * Duplicate team member
 * POST /api/team-member/:teamId/members/:userId/duplicate
 * @param {string} teamId - Team ID
 * @param {string} userId - User ID of member to duplicate
 * @param {Object} options - { count, includeAllCards }
 * @returns {Promise<Object>} Duplicated members data
 */
export const duplicateTeamMember = async (teamId, userId, options) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Duplicating team member:', { teamId: validTeamId, userId, options });

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${userId}/duplicate`,
            options
        );

        if (response.status === 200 || response.status === 201) {
            console.log('[TeamAPI] Member duplicated successfully:', response.data);
            return response.data;
        } else {
            console.error(`[TeamAPI] Unexpected status code: ${response.status}`);
            throw new Error('Failed to duplicate member with unexpected status code');
        }
    } catch (error) {
        console.error('[TeamAPI] Error duplicating member:', error);
        handleTeamApiError(error, 'Duplicating member');
        throw error;
    }
};

// Remove Member from Team (Removes from team only, user account remains)
export const removeTeamMember = async (teamId, userId) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Removing team member:', { teamId: validTeamId, userId });

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${userId}`
        );

        if (response.status === 200) {
            console.log('[TeamAPI] Member removed successfully:', response.data);
            return response.data;
        } else {
            console.error(`[TeamAPI] Unexpected status code: ${response.status}`);
            throw new Error('Failed to remove member with unexpected status code');
        }
    } catch (error) {
        console.error('[TeamAPI] Error removing member:', error);
        handleTeamApiError(error, 'Removing member');
        throw error;
    }
};

// Delete Member (Permanently Delete User and All Cards)
export const deleteTeamMember = async (teamId, userId) => {
    try {
        const validTeamId = validateTeamId(teamId);

        console.log('[TeamAPI] Deleting team member:', { teamId: validTeamId, userId });

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team-member/${validTeamId}/members/${userId}/delete`
        );

        if (response.status === 200) {
            console.log('[TeamAPI] Member deleted successfully:', response.data);
            return response.data;
        } else {
            console.error(`[TeamAPI] Unexpected status code: ${response.status}`);
            throw new Error('Failed to delete member with unexpected status code');
        }
    } catch (error) {
        console.error('[TeamAPI] Error deleting member:', error);
        handleTeamApiError(error, 'Deleting member');
        throw error;
    }
};

/**
 * Send email notification to team members
 * POST /api/team/:teamId/notifications/email
 * @param {string} teamId - Team ID
 * @param {Object} data - { subject, message, recipients, scheduleDate, scheduleTime }
 * @returns {Promise<Object>} Sent notification details
 */
export const sendTeamEmailNotification = async (teamId, data) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Sending email notification:', { teamId: validTeamId, subject: data.subject });
        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/notifications/email`,
            data
        );
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error sending email notification:', error);
        handleTeamApiError(error, 'Sending email notification');
    }
};

/**
 * Get team notification history
 * GET /api/team/:teamId/notifications
 * @param {string} teamId - Team ID
 * @param {number} page - Page number
 * @param {number} limit - Limit per page
 * @returns {Promise<Object>} List of notifications
 */
export const getTeamNotifications = async (teamId, page = 1, limit = 10) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Getting notification history:', validTeamId);
        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/notifications`,
            { params: { page, limit } }
        );
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error getting notification history:', error);
        handleTeamApiError(error, 'Getting notification history');
    }
};

/**
 * Send push notification to team members
 * POST /api/team/:teamId/notifications/push
 * @param {string} teamId - Team ID
 * @param {Object} data - { subject, message, recipients }
 * @returns {Promise<Object>} Sent notification details
 */
export const sendTeamPushNotification = async (teamId, data) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Sending push notification:', { teamId: validTeamId, subject: data.subject });
        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/notifications/push`,
            data
        );
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error sending push notification:', error);
        handleTeamApiError(error, 'Sending push notification');
    }
};

/**
 * Save FCM token for the current user
 * POST /api/team/:teamId/notifications/fcm-token
 * @param {string} teamId - Team ID
 * @param {string} token - FCM Token
 * @returns {Promise<Object>} Success message
 */
export const saveTeamFcmToken = async (teamId, token) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/notifications/fcm-token`,
            { token }
        );
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error saving FCM token:', error);
        handleTeamApiError(error, 'Saving FCM token');
    }
};

/**
 * Get API keys for a team
 * GET /api/team/:teamId/api-keys
 * @param {string} teamId - Team ID
 * @returns {Promise<Object>} List of API keys
 */
export const getTeamApiKeys = async (teamId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Getting API keys:', validTeamId);
        const response = await apiClient.get(`${API_BASE_URL}/api/team/${validTeamId}/api-keys`);
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error getting API keys:', error);
        handleTeamApiError(error, 'Getting API keys');
    }
};

/**
 * Create a new API key
 * POST /api/team/:teamId/api-keys
 * @param {string} teamId - Team ID
 * @param {Object} data - { label }
 * @returns {Promise<Object>} Created API key
 */
export const createTeamApiKey = async (teamId, data) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Creating API key:', { teamId: validTeamId, label: data.label });
        const response = await apiClient.post(`${API_BASE_URL}/api/team/${validTeamId}/api-keys`, data);
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error creating API key:', error);
        handleTeamApiError(error, 'Creating API key');
    }
};

/**
 * Delete an API key
 * DELETE /api/team/:teamId/api-keys/:keyId
 * @param {string} teamId - Team ID
 * @param {string} keyId - API Key ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteTeamApiKey = async (teamId, keyId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        console.log('[TeamAPI] Deleting API key:', { teamId: validTeamId, keyId });
        const response = await apiClient.delete(`${API_BASE_URL}/api/team/${validTeamId}/api-keys/${keyId}`);
        return response.data;
    } catch (error) {
        console.error('[TeamAPI] Error deleting API key:', error);
        handleTeamApiError(error, 'Deleting API key');
    }
};

export const updateTeamMemberRole = async (teamId, userId, role) => {
    try {
        const response = await apiClient.put(
            `${API_BASE_URL}/api/team-member/${teamId}/members/${userId}/role`,
            { role }
        );
        return response.data;
    } catch (error) {
        handleTeamApiError(error, 'Updating member role');
    }
};

export default {
    createTeam,
    getTeam,
    updateTeam,
    deleteTeam,
    getTeamStats,
    getUserTeams,
    duplicateTeamMember,
    removeTeamMember,
    deleteTeamMember,
    sendTeamEmailNotification,
    sendTeamPushNotification,
    getTeamNotifications,
    saveTeamFcmToken,
    getTeamApiKeys,
    createTeamApiKey,
    deleteTeamApiKey,
    updateTeamMemberRole
};